import {
  Check,
  CheckCheck,
  CircleAlert,
  Clock3,
  TriangleAlert,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { BadgeTone } from '~/components/ui/badge';
import type { RouterOutputs } from '~/lib/trpc';
import { Badge } from '~/components/ui/badge';

/** One rollup, built by one helper server-side, so the mailbox and the feed
 *  cannot disagree about how a mail went. */
export type DeliveryBadgeValue = NonNullable<
  RouterOutputs['emails']['views']['getIncomingThread']
>['emails'][number]['deliveryBadge'];

type BadgeKind = NonNullable<DeliveryBadgeValue>['kind'];

const badgeStyles = {
  bounced: { icon: CircleAlert, tone: 'destructive' },
  delayed: { icon: Clock3, tone: 'warning' },
  delivered: { icon: CheckCheck, tone: 'neutral' },
  partly_delivered: { icon: TriangleAlert, tone: 'warning' },
  pending: { icon: Clock3, tone: 'neutral' },
  sent: { icon: Check, tone: 'neutral' },
} as const satisfies Record<BadgeKind, { icon: typeof Check; tone: BadgeTone }>;

/**
 * How a mail went, in the words both surfaces use. Derived on read from
 * `deliveries[]`, so a single-recipient send degenerates to the four plain
 * labels.
 *
 * Only the states worth acting on carry a colour. A delivered mail is every
 * mail, so a green one on each row is a colour that never means anything.
 */
function useDeliveryStatus(badge: DeliveryBadgeValue) {
  const { t } = useTranslation();

  if (!badge) {
    return null;
  }

  const labels = {
    bounced: t('Bounced'),
    delayed: t('Delayed'),
    delivered: t('Delivered'),
    partly_delivered: t('Partly delivered ({{delivered}}/{{total}})', {
      delivered: badge.delivered,
      total: badge.total,
    }),
    pending: t('Pending'),
    sent: t('Sent'),
  };

  return { ...badgeStyles[badge.kind], label: labels[badge.kind] };
}

/** The mailbox's pill, beside a message that has a surface of its own. */
export function DeliveryBadge({ badge }: { badge: DeliveryBadgeValue }) {
  const status = useDeliveryStatus(badge);

  if (!status) {
    return null;
  }

  return (
    <Badge tone={status.tone}>
      <status.icon />
      {status.label}
    </Badge>
  );
}

/** The same fact with no fill of its own, for the feed: a bubble is already
 *  coloured by who sent the message, and a pill on top of that fights it. */
export function DeliveryStatus({ badge }: { badge: DeliveryBadgeValue }) {
  const status = useDeliveryStatus(badge);

  if (!status) {
    return null;
  }

  return (
    <span className="flex shrink-0 items-center gap-1">
      <status.icon className="size-3" strokeWidth={2.5} />
      {status.label}
    </span>
  );
}
